import { useCallback, useEffect, useState } from 'react';
import { DatePicker, Popover, Select, TextField } from '@shopify/polaris';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';

dayjs.extend(objectSupport);

type Props = {
    value: string;
    onChange: (value: string) => void;
    title?: string;
    withTime?: boolean;
};

export function CustomDatePicker({ value, onChange, title = '', withTime = false }: Readonly<Props>) {
    const [popoverActive, setPopoverActive] = useState(false);
    const togglePopoverActive = useCallback(() => setPopoverActive(popoverActive => !popoverActive), []);
    const [year, setYear] = useState(dayjs(value).year());
    const [month, setMonth] = useState(dayjs(value).month());
    const [day, setDay] = useState(dayjs(value).date());
    const [hour, setHour] = useState(dayjs(value).hour() % 12);
    const [minute, setMinute] = useState(dayjs(value).minute());
    const [amPm, setAmPm] = useState(dayjs(value).hour() >= 12 ? 'PM' : 'AM');

    const activator = (
        <div onClick={togglePopoverActive}>
            <TextField label={title} value={value ? dayjs(value).format('MMM D YYYY') : ''} autoComplete="off" />
        </div>
    );

    const handleDateChange = ({ end }: { start: Date; end: Date }) => {
        const date = dayjs(end);
        setYear(date.year());
        setMonth(date.month());
        setDay(date.date());
        togglePopoverActive();
    };

    const handleMonthChange = useCallback((month: number, year: number) => {
        setMonth(month);
        setYear(year);
    }, []);

    useEffect(() => {
        onChange(
            dayjs({
                year,
                month,
                day,
                hour: amPm === 'PM' ? (hour % 12) + 12 : hour % 12,
                minute,
                second: 0,
                millisecond: 0,
            }).toISOString()
        );
    }, [year, month, day, hour, minute, amPm]);

    useEffect(() => {
        if (value) {
            const date = dayjs(value);
            setYear(date.year());
            setMonth(date.month());
            setDay(date.date());
            setHour(date.hour() % 12);
            setMinute(date.minute());
            setAmPm(date.hour() >= 12 ? 'PM' : 'AM');
        }
    }, [value]);

    return (
        <div style={{ display: 'flex', gap: '8px', flexFlow: 'column' }}>
            <Popover
                active={popoverActive}
                activator={activator}
                autofocusTarget="first-node"
                onClose={togglePopoverActive}
            >
                <DatePicker
                    month={month}
                    year={year}
                    onChange={handleDateChange}
                    onMonthChange={handleMonthChange}
                    selected={new Date(value)}
                />
            </Popover>
            {withTime && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <TextField
                        label=""
                        autoComplete="off"
                        value={String(hour)}
                        onChange={v => setHour(Number(v) || 0)}
                        type="number"
                        min={0}
                        max={12}
                    />
                    <TextField
                        label=""
                        autoComplete="off"
                        value={String(minute)}
                        onChange={v => setMinute(Number(v) || 0)}
                        type="number"
                        min={0}
                        max={59}
                    />
                    <Select
                        label=""
                        value={amPm}
                        onChange={setAmPm}
                        options={[
                            { label: 'AM', value: 'AM' },
                            { label: 'PM', value: 'PM' },
                        ]}
                    />
                </div>
            )}
        </div>
    );
}
